import React, { useState, useEffect } from "react";
import "./DynamicPassportDetailsTable.css";

interface PassportEntry {
  passportNationality: string;
  passportNumber: string;
  issueDate: string;
  expiryDate: string;
}

interface DynamicPassportDetailsTableProps {
  value: { [key: string]: PassportEntry };
  onChange: (entries: { [key: string]: PassportEntry }) => void;
  required?: boolean;
}

const DynamicPassportDetailsTable: React.FC<
  DynamicPassportDetailsTableProps
> = ({ value, onChange, required = false }) => {
  const [entries, setEntries] = useState<{ [key: string]: PassportEntry }>(
    value || {}
  );

  useEffect(() => {
    onChange(entries);
  }, [entries, onChange]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "Enter") {
        addEntry();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `passport${entryCount + 1}`;

    const newEntry: PassportEntry = {
      passportNationality: "",
      passportNumber: "",
      issueDate: "",
      expiryDate: "",
    };

    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;

    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: PassportEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();

    remainingKeys.forEach((oldKey, index) => {
      const newKey = `passport${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });

    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (
    key: string,
    field: keyof PassportEntry,
    value: string
  ) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value },
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  // Initialize with at least one entry if empty
  useEffect(() => {
    if (Object.keys(entries).length === 0) {
      addEntry();
    }
  }, []);

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="passport-details-table">
      <div className="table-header">
        <h4>Passport Details</h4>
        <button
          type="button"
          className="add-btn"
          onClick={addEntry}
          title="Add passport entry (Ctrl+Enter)"
        >
          + Add Passport Entry
        </button>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No passport information entered yet.</p>
          <button type="button" className="add-btn" onClick={addEntry}>
            + Add Passport Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this passport entry"
                >
                  ×
                </button>
              </div>

              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Passport Nationality</label>
                    <input
                      type="text"
                      value={entries[key].passportNationality}
                      onChange={(e) =>
                        updateEntry(key, "passportNationality", e.target.value)
                      }
                      placeholder="e.g., Pakistani"
                    />
                  </div>

                  <div className="field-group">
                    <label>Passport Number</label>
                    <input
                      type="text"
                      value={entries[key].passportNumber}
                      onChange={(e) =>
                        updateEntry(key, "passportNumber", e.target.value)
                      }
                      placeholder="e.g., SF4151501"
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="field-group">
                    <label>Issue Date</label>
                    <input
                      type="date"
                      value={entries[key].issueDate}
                      onChange={(e) =>
                        updateEntry(key, "issueDate", e.target.value)
                      }
                      placeholder="yyyy-MM-dd"
                    />
                  </div>

                  <div className="field-group">
                    <label>Expiry Date</label>
                    <input
                      type="date"
                      value={entries[key].expiryDate}
                      onChange={(e) =>
                        updateEntry(key, "expiryDate", e.target.value)
                      }
                      placeholder="yyyy-MM-dd"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {entryKeys.length > 0 && (
        <div className="table-footer">
          <button
            type="button"
            className="add-btn"
            onClick={addEntry}
            title="Add another passport entry (Ctrl+Enter)"
          >
            + Add Another Passport Entry
          </button>
        </div>
      )}

      {required && entryKeys.length === 0 && (
        <small className="error-text">
          At least one passport entry is required
        </small>
      )}
    </div>
  );
};

export default DynamicPassportDetailsTable;
